import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { IHomeSectionResponse } from '../../../../model/apm/home/response/home-section-response';
import { buildImageUrl } from '../../components/image-utils';
import { PREVIEW_BANNERS, PREVIEW_NEWS, sampleImage } from './home-preview-samples';

const MAX_THUMBS = 4;

function Thumb({ src, title, width, ratio, caption }: { src: string; title?: string; width: number; ratio: string; caption?: string }) {
    return (
        <Box sx={{ width, flexShrink: 0 }}>
            <Box sx={{ position: 'relative', width: '100%', aspectRatio: ratio, borderRadius: 1, overflow: 'hidden', boxShadow: 1 }}>
                <Box component="img" src={src} alt={title} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                {title && (
                    <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, px: 1, py: 0.5, background: 'linear-gradient(to top, rgba(0,0,0,.65), transparent)' }}>
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, display: 'block' }} noWrap>{title}</Typography>
                    </Box>
                )}
            </Box>
            {caption && <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ mt: 0.5 }}>{caption}</Typography>}
        </Box>
    );
}

function MockupRow({ children, note }: { children: React.ReactNode; note: string }) {
    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5 }}>{children}</Box>
            <Typography variant="caption" color="text.disabled">{note}</Typography>
        </Box>
    );
}

export function BannerMockup({ section }: { section: IHomeSectionResponse }) {
    const items = section.items || [];
    const slides = items.length > 0
        ? items.map((item, i) => ({ title: item.title, image: buildImageUrl(item.image) || sampleImage(i, 'banner'), dimmed: !item.isVisible }))
        : PREVIEW_BANNERS.map((b) => ({ title: b.title, image: b.image, dimmed: false }));
    const extra = slides.length - MAX_THUMBS;

    return (
        <MockupRow note={items.length > 0 ? 'Imagens ilustrativas — a ordem segue a lista de banners' : 'Exemplo — sem banner cadastrado a seção não aparece no portal'}>
            {slides.slice(0, MAX_THUMBS).map((s, i) => (
                <Box key={i} sx={{ opacity: s.dimmed ? 0.45 : 1 }}>
                    <Thumb src={s.image} title={s.title} width={220} ratio="16 / 5" />
                </Box>
            ))}
            {extra > 0 && (
                <Box sx={{ width: 80, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, border: '1px dashed', borderColor: 'divider', aspectRatio: '1' }}>
                    <Typography variant="body2" color="text.secondary">+{extra}</Typography>
                </Box>
            )}
        </MockupRow>
    );
}

export function NewsMockup({ section }: { section: IHomeSectionResponse }) {
    const quantity = section.itemsQuantity ?? MAX_THUMBS;
    const shown = Math.min(quantity, MAX_THUMBS);
    const extra = quantity - shown;

    return (
        <MockupRow note={`Exemplo — exibe as ${quantity} notícias mais recentes publicadas no portal`}>
            {Array.from({ length: shown }).map((_, i) => {
                const news = PREVIEW_NEWS[i % PREVIEW_NEWS.length];
                return <Thumb key={i} src={news.image} width={150} ratio="16 / 10" caption={`${dayjs(news.date).format('DD/MM')} · ${news.title}`} />;
            })}
            {extra > 0 && (
                <Box sx={{ width: 80, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, border: '1px dashed', borderColor: 'divider', height: 94 }}>
                    <Typography variant="body2" color="text.secondary">+{extra}</Typography>
                </Box>
            )}
        </MockupRow>
    );
}

export function GalleryMockup({ section }: { section: IHomeSectionResponse }) {
    const items = (section.items || []).filter((item) => !!item.image);
    const extra = items.length - MAX_THUMBS;

    return (
        <MockupRow note="Imagens ilustrativas — a ordem segue a lista de imagens">
            {items.slice(0, MAX_THUMBS).map((item, i) => (
                <Box key={item.id} sx={{ opacity: item.isVisible ? 1 : 0.45 }}>
                    <Thumb src={buildImageUrl(item.image) || sampleImage(i)} width={120} ratio="4 / 3" caption={item.title} />
                </Box>
            ))}
            {extra > 0 && (
                <Box sx={{ width: 80, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, border: '1px dashed', borderColor: 'divider', height: 90 }}>
                    <Typography variant="body2" color="text.secondary">+{extra}</Typography>
                </Box>
            )}
        </MockupRow>
    );
}
